import React, { useMemo } from 'react';
import { EtapaResult } from './types';
import TabelaSistema, { ColunaTabela } from './TabelaSistema';
import { duracaoLegivel, formatarDataHoraCurta } from './statusBadge';

const ROTULO_ETAPA: Record<EtapaResult['status'], string> = {
  EM_ANDAMENTO: 'Em andamento',
  OK: 'OK',
  FALHA_PARCIAL: 'Falha parcial',
  FALHOU: 'Falhou',
  PULADA: 'Pulada',
};

const CLASSE_ETAPA: Record<EtapaResult['status'], string> = {
  EM_ANDAMENTO: 'badge-azul',
  OK: 'badge-verde',
  FALHA_PARCIAL: 'badge-laranja',
  FALHOU: 'badge-vermelho',
  PULADA: 'badge-cinza',
};

/** Progresso textual publicado DENTRO de uma etapa em andamento (ex: "12000/34550 registros"). */
const progressoDaEtapa = (detalhe: unknown): string | null => (typeof detalhe === 'string' ? detalhe : null);

/**
 * Rótulos das chaves que os syncs devolvem no `detalhe` de uma etapa concluída. Só o que
 * está aqui é exibido: chave desconhecida fica de fora em vez de vazar nome de campo para
 * a tela (a regra do projeto proíbe jargão de código em texto visível).
 */
const ROTULO_DETALHE: Record<string, string> = {
  inseridos: 'inseridos',
  atualizados: 'atualizados',
  semAlteracao: 'sem alteração',
  descartados: 'descartados',
  ignorados: 'ignorados',
  janelasProcessadas: 'janelas',
  paginasProcessadas: 'páginas',
  total: 'total',
};

/**
 * Resumo legível do `detalhe` de uma etapa concluída — que é um OBJETO com as contagens do
 * sync, e por isso não aparecia em lugar nenhum da interface: a coluna de progresso só
 * renderiza `detalhe` quando ele é string (o caso do EM_ANDAMENTO). Sem isto, uma execução
 * de eventos que gravou 376 registros mostrava só a duração, e "quantos entraram?" só era
 * respondível pelo banco.
 *
 * Genérico de propósito — serve os dois painéis (base e FIPE) e qualquer etapa cujo
 * resultado traga essas contagens.
 */
const resumirDetalhe = (detalhe: unknown): string | null => {
  if (!detalhe || typeof detalhe !== 'object' || Array.isArray(detalhe)) return null;
  const d = detalhe as Record<string, unknown>;
  const partes = Object.entries(ROTULO_DETALHE)
    .filter(([chave]) => typeof d[chave] === 'number')
    .map(([chave, rotulo]) => `${(d[chave] as number).toLocaleString('pt-BR')} ${rotulo}`);

  // Base cujo token não tem o tópico liberado no SGA. Entra como INFORMAÇÃO, não como
  // problema: a etapa é OK e nenhum reprocessamento muda isso.
  const semPermissao = Array.isArray(d.basesSemPermissao)
    ? (d.basesSemPermissao as Array<{ base?: string }>).map((b) => b?.base).filter(Boolean)
    : [];
  if (semPermissao.length) partes.push(`sem acesso: ${semPermissao.join(', ')}`);

  return partes.length ? partes.join(' · ') : null;
};

const detalheDaEtapa = (e: EtapaResult): string => {
  const base =
    e.status === 'EM_ANDAMENTO'
      ? progressoDaEtapa(e.detalhe) ?? `desde ${formatarDataHoraCurta(e.startedAt)}`
      : duracaoLegivel(e.startedAt, e.finishedAt) || '-';
  return e.erro ? `${base} — ${e.erro}` : base;
};

/**
 * Etapas de um job na tabela padrão do sistema. O `resultado.etapas` já vem pronto do
 * backend: concluídas com horários, a atual como EM_ANDAMENTO. Etapa re-tentada pelo
 * retry interno aparece repetida de propósito — o histórico de tentativas é dado.
 */
const EtapasTimeline: React.FC<{ etapas: EtapaResult[] }> = ({ etapas }) => {
  const colunas = useMemo<ColunaTabela<EtapaResult>[]>(
    () => [
      { titulo: 'Etapa', largura: 240, texto: (e) => e.etapa },
      {
        titulo: 'Situação',
        largura: 130,
        texto: (e) => ROTULO_ETAPA[e.status] ?? e.status,
        render: (e) => (
          <span className={`cilia-badge ${CLASSE_ETAPA[e.status] ?? 'badge-cinza'}`}>
            {ROTULO_ETAPA[e.status] ?? e.status}
          </span>
        ),
      },
      { titulo: 'Duração / progresso', largura: 200, texto: detalheDaEtapa },
      { titulo: 'Resultado', largura: 260, texto: (e) => resumirDetalhe(e.detalhe) ?? '-' },
      { titulo: 'Início', largura: 140, texto: (e) => formatarDataHoraCurta(e.startedAt) },
    ],
    [],
  );

  return (
    <TabelaSistema
      colunas={colunas}
      itens={etapas}
      chave={(e) => `${e.etapa}-${e.startedAt}-${e.status}`}
      vazio="Nenhuma etapa registrada ainda para esta execução."
    />
  );
};

export default EtapasTimeline;
